import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import { isDefined } from '@/definitions/services/helpers';
import {IFormLayoutItem, IFormModel} from '@/uikit/forms/NForm.vue';
import { configureConfidence, configureConfidenceRange, SimpleFilterOptions } from './types';
import { ListViewModel } from '@/definitions/view-models';
import { dataModule } from '@/store/data';
import { WatchList } from '@/api';
import { aclModule } from '@/store/acl';

export type Position = typeof Positions[keyof typeof Positions];
export const Positions = {
  Start: 'start',
  End: 'end'
} as const;

export function isValidIpV4Address(address: string): boolean {
  return /(\b25[0-5]|\b2[0-4][0-9]|\b[01]?[0-9][0-9]?)(\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}/.test(address);
}

export function isValidIpV6Address(address: string): boolean {
  return /(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/.test(
    address
  );
}

export function addRowClasses(options: SimpleFilterOptions, position: Position) {
  const rowClass = `n-form-item__${options.small ? 'small' : 'large'}_${position}`;
  const classes = options.classes ? `${options.classes} ${rowClass}` : rowClass;
  return { ...options, classes };
}

export function decodeScore(value: number | null | undefined) {
  return isNumber(value) ? value / 100 : value;
}

export function entcodeScore(v: number | null) {
  return isNil(v) ? v : Math.round(v * 100);
}

export type ConfidenceOptions = {
  confidenceFieldName: string;
  primaryFieldName: string;
  configPrimaryField: (options: SimpleFilterOptions) => IFormLayoutItem;
};

const nameDelimiter = '__';
export function encodeFormItemName(parts: string[]) {
  return parts.join(nameDelimiter);
}

export function decodeFormItemName(name: string) {
  return name.split(nameDelimiter);
}

export function getListFieldWithConfidence(options: SimpleFilterOptions, confidenceOptions: ConfidenceOptions): IFormLayoutItem[] {
  const { confidenceFieldName, primaryFieldName, configPrimaryField } = confidenceOptions;
  const fieldOptions = {
    ...addRowClasses(options, Positions.Start)
  };

  const confidenceFormOptions = {    
    ...addRowClasses(options, Positions.End),
    path: confidenceFieldName,
    i18n_label: undefined,
    checkForEmpty: (v: any) => !(isDefined(v?.[primaryFieldName]) && v?.[primaryFieldName].length)
  };

  const [primaryField, confidenceField] = [configPrimaryField(fieldOptions), configureConfidence(confidenceFormOptions)];
  function encode(model: IFormModel, value: string[] | null) {
    model[primaryFieldName] = value;
    if (!value?.length) delete model[confidenceFieldName];
  }
  function decode(model: IFormModel) {
    return model[primaryFieldName];
  }
  primaryField.encode = encode;
  primaryField.decode = decode;
  return [primaryField, confidenceField];
}

export function getExcludeFilterFunctionById(ids: any[]) {
  return (v: any) => !ids.includes(v.id);
}

export function watchListMapFunction(v: WatchList) {
  return {
    label: v.name,
    value: v.id,
    color: '#' + v.color,
    disabled: !aclModule.getIsEditableWatchList(v.id)
  };
}

export function generateWatchListsLoadSearchMethods () {
  return generateLoadSearchMethodsByLVM(dataModule.watchListsModule, {
    mapFunction: watchListMapFunction
  });
}

export function generateWatchListsWithoutUnknownLoadSearchMethods () {
  return generateLoadSearchMethodsByLVM(dataModule.watchListsModule, {
    mapFunction: watchListMapFunction,
    filterFunction: getExcludeFilterFunctionById([-1])
  });
}

export function generateLoadSearchMethodsByLVM(model: ListViewModel<any, any>, options?: any) {
  let { filters, mapFunction, filterFunction } = options || {};
  const defaultMapFunction = (v: any) => ({ label: v.name, value: v.id });
  const defaultFilterFunction = (v: any) => true;
  mapFunction = mapFunction || defaultMapFunction;
  filterFunction = filterFunction || defaultFilterFunction;

  return {
    loadItems: async (id_in?: number[]) => {
      const response = await model.dataService.getList({ id_in, limit: id_in?.length ? 1000 : 20 } as any);
      const items = response.results;
      return items?.map(mapFunction);
    },
    searchItems: async (name_contains?: string) => {
      const response = await model.dataService.getList({ name_contains, limit: 20, ...filters });
      const items = response.results;
      return items?.filter(filterFunction).map(mapFunction);
    }
  };
}